import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import classNames from 'classnames';
import { isMobile, SIZE_MD } from '../../../../hooks/useBreakPoint';

import { getGenericError } from '../../utils/errors';
import { getStores } from '../../utils/service';
import { getTrackerId, POSTHOG_CAPTURE_ATTRIBUTES, TRACKER_IDS } from '../../config/tracker';
import { pushSelectStoreEvent } from '../../utils/gtm';
import { useFirebaseAuth } from '../../../global/FirebaseProvider/FirebaseProvider';
import {
  ANCHOR_DEALS,
  CATEGORY_SEARCH_PARAM,
  ROUTE_REWARDS_V2_LIST,
  ROUTE_REWARDS_V2_STORE_ID,
  STORE_ID_ROUTE_PARAM,
} from '../../config/routes';
import { Button, BUTTON_SIZE, BUTTON_TYPE } from '../Button';
import LoadingBar from '../LoadingBar';
import RewardsStoreCard from '../RewardsStoreCard';
import SliderV3 from '../SliderV3';
import { SLIDER_TRACK_PADDING } from '../../config/config';
import ArrowRightSvg from '../SvgComponents/ArrowRight_svg';

import './RewardsStoresShelf.scss';

const {
  dashboard: {
    instantDeals: {
      viewAllButton: trackerViewAllButton,
    },
  },
} = TRACKER_IDS;

const SLIDES_PER_PAGE_DEFAULT = 6;
const SLIDES_PER_PAGE_MOBILE_MD = 4;
const SLIDES_PER_PAGE_MOBILE = 2;

const RewardsStoresShelf = ({
  title,
  category,
  showLoading = true,
  showOnEmptyResults = false,
  trackerProps = {},
  defaultSlidesPerPage = SLIDES_PER_PAGE_DEFAULT,
  fullWidth = false,
  gap = '1rem',
  mobileGap = '1rem',
  advancedMode = false,
  trackPadding = SLIDER_TRACK_PADDING.SMALL,
}) => {
  const history = useHistory();
  const { userData } = useFirebaseAuth();
  const mobile = isMobile();
  const mobileMD = isMobile([SIZE_MD]);

  const [stores, setStores] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEmptyState, setIsEmptyState] = useState(false);

  const loadStores = useCallback(async (categoryId) => {
    setIsLoading(true);

    const { data, error: storesError } = await getStores({
      pageSize: 10,
      page: 1,
      ...(categoryId && { categoryId }),
    });

    if (storesError) {
      setIsEmptyState(true);
      setStores([]);
      setError(storesError?.message || getGenericError());
      setIsLoading(false);
      return;
    }

    setStores(data.stores);
    setIsEmptyState(!data.stores.length);
    setError('');
    setIsLoading(false);
  }, []);

  const handleOnClick = useCallback(() => {
    history.push({
      pathname: ROUTE_REWARDS_V2_LIST,
      search: category?.routeName
        ? new URLSearchParams({ [CATEGORY_SEARCH_PARAM]: category.routeName }).toString()
        : undefined,
    });
  }, [history, category?.routeName]);

  const handleItemOnClick = useCallback((store, index) => {
    pushSelectStoreEvent(store, userData, index);
    history.push(ROUTE_REWARDS_V2_STORE_ID.replace(STORE_ID_ROUTE_PARAM, store.id));
  }, [history, userData]);

  useEffect(() => {
    loadStores(category?.uid);
  }, [category?.uid]);

  const emptyLoading = !showLoading && isLoading;
  const emptyState = showOnEmptyResults && isEmptyState;

  if (emptyLoading || emptyState) {
    return null;
  }

  return (
    <div
      id={`${ANCHOR_DEALS}-stores-${category?.uid ?? 'all'}`}
      className={classNames('RewardsStoresShelf', {
        'RewardsStoresShelf--full-width': fullWidth,
      })}
    >
      <div className="RewardsStoresShelf__container">
        <div className={classNames('RewardsStoresShelf__container-top', trackPadding, {
          'RewardsStoresShelf--no-margin': fullWidth,
        })}
        >
          {title && (
            <h2 className="RewardsStoresShelf__container-top-text">{title}</h2>
          )}
          <Button
            size={BUTTON_SIZE.LARGE}
            type={BUTTON_TYPE.LINK_QUINARY}
            onClick={handleOnClick}
            className="RewardsStoresShelf__container-top-cta"
            trackerProps={{
              [POSTHOG_CAPTURE_ATTRIBUTES.TRACKER_ID]: getTrackerId(trackerViewAllButton),
              ...(category && {
                [POSTHOG_CAPTURE_ATTRIBUTES.CATEGORY_ID]: category?.uid,
              }),
            }}
          >
            <ArrowRightSvg color="var(--trashie-ui-faded-black-90)" />
          </Button>
        </div>
        <div className="RewardsStoresShelf__container-content">
          {isLoading ? (
            <LoadingBar className="RewardsStoresShelf__container-loader" />
          ) : (
            error ||
            (stores.length === 0 && 'No stores available at the moment') ||
            (
              <SliderV3
                id={`rewards-stores-shelf-${category?.uid ?? 'all'}`}
                slidesPerPage={mobileMD
                  ? SLIDES_PER_PAGE_MOBILE_MD
                  : (mobile && SLIDES_PER_PAGE_MOBILE) || defaultSlidesPerPage}
                gap={gap}
                mobileGap={mobileGap}
                trackPadding={trackPadding}
                advancedMode={advancedMode}
                slides={stores.map((store, index) => ({
                  key: `rewards-stores-shelf-${store.id}`,
                  slide: (
                    <RewardsStoreCard
                      key={`slide-store-${store.id}`}
                      index={index}
                      store={store}
                      onClick={handleItemOnClick}
                      trackerProps={{
                        ...trackerProps,
                        [POSTHOG_CAPTURE_ATTRIBUTES.SECTION]: 'stores-shelf',
                        ...(category && {
                          [POSTHOG_CAPTURE_ATTRIBUTES.CATEGORY]: category?.name,
                          [POSTHOG_CAPTURE_ATTRIBUTES.CATEGORY_ID]: category?.uid,
                        }),
                      }}
                    />
                  ),
                }))}
              />
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default RewardsStoresShelf;
